import {
  createContext,
  forwardRef,
  useContext,
  type AnchorHTMLAttributes,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type ReactNode,
} from 'react';
import type { PublicRentalApplicationLocationState } from './public-route-contracts';

export interface PublicRouteLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  state?: PublicRentalApplicationLocationState;
}

export type PublicRouteLinkComponent = ForwardRefExoticComponent<
  PublicRouteLinkProps & RefAttributes<HTMLAnchorElement>
>;

export interface PublicRouteBridge {
  Link: PublicRouteLinkComponent;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  searchParams: URLSearchParams;
  setSearchParams: (
    updater: (previous: URLSearchParams) => URLSearchParams,
    options?: { replace?: boolean },
  ) => void;
}

const PublicRouteBridgeContext = createContext<PublicRouteBridge | null>(null);

export function PublicRouteBridgeProvider({
  bridge,
  children,
}: {
  bridge: PublicRouteBridge;
  children: ReactNode;
}) {
  return (
    <PublicRouteBridgeContext.Provider value={bridge}>
      {children}
    </PublicRouteBridgeContext.Provider>
  );
}

export function usePublicRouteBridge(): PublicRouteBridge {
  const bridge = useContext(PublicRouteBridgeContext);
  if (!bridge) {
    throw new Error('Public route core must be rendered inside PublicRouteBridgeProvider');
  }

  return bridge;
}

export const PublicRouteLink = forwardRef<HTMLAnchorElement, PublicRouteLinkProps>(
  function PublicRouteLink(props, ref) {
    const { Link } = usePublicRouteBridge();
    return <Link {...props} ref={ref} />;
  },
);

export function searchParamsFromObject(search: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(search).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) params.append(key, String(item));
      });
      return;
    }

    params.set(key, String(value));
  });

  return params;
}

export function objectFromSearchParams(params: URLSearchParams): Record<string, string | string[]> {
  const values: Record<string, string | string[]> = {};
  params.forEach((value, key) => {
    const current = values[key];
    if (current === undefined) {
      values[key] = value;
    } else if (Array.isArray(current)) {
      current.push(value);
    } else {
      values[key] = [current, value];
    }
  });

  return values;
}
